import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import type { AssetEntityType, LogEntityType } from '@houseops/shared';

/**
 * Shared app-shell state. Concerns that need to be reachable from
 * any descendant page:
 *
 *   - Global Capture sheet (bottom-nav center button).
 *   - Global Search sheet (bottom-nav search cell).
 *   - Global LogForm sheet, optionally prefilled by a caller (e.g.
 *     a checklist run's "Log this run" button).
 *   - Global PhotoUpload sheet, similarly prefilled.
 *   - Per-route FAB action: pages register a primary action via
 *     `useRegisterFab(action)`, the shell renders it in <Fab />.
 *
 * Kept lightweight — no reducer, no library. useState hooks and
 * stable callbacks.
 */

export interface FabAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

export interface LogIntent {
  domainId?: string;
  zoneId?: string | null;
  entityType?: LogEntityType | null;
  entityId?: string | null;
  title?: string;
  notes?: string | null;
}

export interface PhotoIntent {
  domainId?: string | null;
  zoneId?: string | null;
  entityType?: AssetEntityType | null;
  entityId?: string | null;
  tags?: string[];
}

interface AppShellState {
  captureOpen: boolean;
  searchOpen: boolean;
  fab: FabAction | null;
  logFormOpen: boolean;
  logIntent: LogIntent | null;
  photoSheetOpen: boolean;
  photoIntent: PhotoIntent | null;
  openCapture: () => void;
  closeCapture: () => void;
  openSearch: () => void;
  closeSearch: () => void;
  openLogForm: (intent?: LogIntent) => void;
  closeLogForm: () => void;
  openPhotoSheet: (intent?: PhotoIntent) => void;
  closePhotoSheet: () => void;
  setFab: (next: FabAction | null) => void;
}

const Ctx = createContext<AppShellState | null>(null);

export function AppShellProvider({ children }: { children: ReactNode }) {
  const [captureOpen, setCaptureOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [fab, setFabAction] = useState<FabAction | null>(null);
  const [logFormOpen, setLogFormOpen] = useState(false);
  const [logIntent, setLogIntent] = useState<LogIntent | null>(null);
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [photoIntent, setPhotoIntent] = useState<PhotoIntent | null>(null);

  const openCapture = useCallback(() => setCaptureOpen(true), []);
  const closeCapture = useCallback(() => setCaptureOpen(false), []);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const openLogForm = useCallback((intent?: LogIntent) => {
    setLogIntent(intent ?? null);
    setLogFormOpen(true);
  }, []);
  const closeLogForm = useCallback(() => {
    setLogFormOpen(false);
    setLogIntent(null);
  }, []);
  const openPhotoSheet = useCallback((intent?: PhotoIntent) => {
    setPhotoIntent(intent ?? null);
    setPhotoSheetOpen(true);
  }, []);
  const closePhotoSheet = useCallback(() => {
    setPhotoSheetOpen(false);
    setPhotoIntent(null);
  }, []);
  const setFab = useCallback((next: FabAction | null) => setFabAction(next), []);

  return (
    <Ctx.Provider
      value={{
        captureOpen,
        searchOpen,
        fab,
        logFormOpen,
        logIntent,
        photoSheetOpen,
        photoIntent,
        openCapture,
        closeCapture,
        openSearch,
        closeSearch,
        openLogForm,
        closeLogForm,
        openPhotoSheet,
        closePhotoSheet,
        setFab,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAppShell(): AppShellState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppShell must be used inside <AppShellProvider>');
  return v;
}
